import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Megaphone, Send, Loader2, Trash2, Eye, EyeOff, Calendar, Users, Building2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BroadcastSender - Componente completo per gestione messaggi broadcast
 * 
 * Funzionalità:
 * - Invio messaggi a tutti o a un reparto specifico
 * - Data di scadenza automatica
 * - Tabella gestione messaggi inviati
 * - Attivazione/disattivazione messaggi
 * - Eliminazione messaggi
 */
export const BroadcastSender = () => {
  const { getAuthHeader } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [reparti, setReparti] = useState([]);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    target_reparto_ids: [],  // Array di ID reparti (vuoto = tutti)
    expires_at: ''  // '' = nessuna scadenza
  });

  // Fetch reparti per dropdown (tutti, inclusa Sede Centrale)
  const fetchReparti = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reparti/all-for-filters`, { headers: getAuthHeader() });
      setReparti(response.data || []);
    } catch (error) {
      console.error('Errore fetch reparti:', error);
    }
  }, [getAuthHeader]);

  // Toggle selezione reparto
  const toggleReparto = (repartoId) => {
    setFormData(prev => ({
      ...prev,
      target_reparto_ids: prev.target_reparto_ids.includes(repartoId)
        ? prev.target_reparto_ids.filter(id => id !== repartoId)
        : [...prev.target_reparto_ids, repartoId]
    }));
  };

  // Fetch broadcasts esistenti
  const fetchBroadcasts = useCallback(async () => {
    setLoadingBroadcasts(true);
    try {
      const response = await axios.get(`${API}/admin/broadcasts`, { headers: getAuthHeader() });
      setBroadcasts(response.data.broadcasts || []);
    } catch (error) {
      console.error('Errore fetch broadcasts:', error);
    } finally {
      setLoadingBroadcasts(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchReparti();
    fetchBroadcasts();
  }, [fetchReparti, fetchBroadcasts]);

  // Invia nuovo messaggio
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setIsSending(true);
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        target_reparto_ids: formData.target_reparto_ids.length > 0 ? formData.target_reparto_ids : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };

      await axios.post(`${API}/admin/broadcasts`, payload, { headers: getAuthHeader() });
      
      let targetName = 'tutta la rete';
      if (formData.target_reparto_ids.length > 0) {
        const selectedNames = reparti
          .filter(r => formData.target_reparto_ids.includes(r.id))
          .map(r => r.nome);
        targetName = selectedNames.join(', ');
      }
      
      toast.success(`Messaggio inviato a ${targetName}!`);
      setFormData({ title: '', content: '', target_reparto_ids: [], expires_at: '' });
      setIsExpanded(false);
      fetchBroadcasts();
    } catch (error) {
      console.error('Errore invio broadcast:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio');
    } finally {
      setIsSending(false);
    }
  };

  // Toggle attivo/disattivo
  const handleToggleActive = async (broadcast) => {
    setActionLoading(broadcast.id);
    try {
      await axios.put(
        `${API}/admin/broadcasts/${broadcast.id}`,
        { is_active: !broadcast.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(broadcast.is_active ? 'Messaggio disattivato' : 'Messaggio riattivato');
      fetchBroadcasts();
    } catch (error) {
      toast.error('Errore durante l\'operazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Elimina messaggio
  const handleDelete = async (broadcastId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo messaggio?')) return;
    
    setActionLoading(broadcastId);
    try {
      await axios.delete(`${API}/admin/broadcasts/${broadcastId}`, { headers: getAuthHeader() });
      toast.success('Messaggio eliminato');
      fetchBroadcasts();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Formatta data per visualizzazione
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Card compatta quando non espansa
  if (!isExpanded) {
    return (
      <Card className="mb-6 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Comunicazioni alla Rete</h3>
                <p className="text-sm text-slate-500">
                  {broadcasts.length} messaggi inviati • Invia o gestisci comunicazioni
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setIsExpanded(true)}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="broadcast-expand-btn"
            >
              <Megaphone className="w-4 h-4 mr-2" />
              Gestisci Messaggi
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Gestione Comunicazioni</CardTitle>
              <CardDescription>
                Invia messaggi alla rete e gestisci quelli esistenti
              </CardDescription>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setIsExpanded(false)}
          >
            Chiudi
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Form Nuovo Messaggio */}
        <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white/50 rounded-lg border border-amber-100">
          <h4 className="font-medium text-slate-700 flex items-center gap-2">
            <Send className="w-4 h-4" />
            Nuovo Messaggio
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="broadcast-title">Titolo *</Label>
              <Input
                id="broadcast-title"
                placeholder="Es: Comunicazione importante"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={200}
                data-testid="broadcast-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Destinatari</Label>
              <div className="border rounded-lg p-3 bg-white max-h-36 overflow-y-auto">
                {/* Opzione "Tutti" */}
                <label className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer border-b mb-2">
                  <input
                    type="checkbox"
                    checked={formData.target_reparto_ids.length === 0}
                    onChange={() => setFormData({ ...formData, target_reparto_ids: [] })}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <Users className="w-4 h-4 text-slate-500" />
                  <span className="font-medium text-slate-700">Tutti i reparti</span>
                </label>
                
                {/* Lista Reparti */}
                {reparti.map(reparto => (
                  <label 
                    key={reparto.id} 
                    className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.target_reparto_ids.includes(reparto.id)}
                      onChange={() => toggleReparto(reparto.id)}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className={`text-sm ${reparto.is_sede_centrale ? 'font-semibold' : ''}`}>
                      {reparto.nome}
                      {reparto.is_sede_centrale && <span className="text-xs text-amber-600 ml-1">(Sede)</span>}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {formData.target_reparto_ids.length === 0 
                  ? 'Il messaggio sarà visibile a tutti'
                  : `Visibile a ${formData.target_reparto_ids.length} reparto/i`
                }
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="broadcast-content">Contenuto *</Label>
            <Textarea
              id="broadcast-content"
              placeholder="Scrivi il messaggio da inviare..."
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={3}
              maxLength={2000}
              className="resize-none bg-white"
              data-testid="broadcast-content-input"
            />
            <p className="text-xs text-slate-400 text-right">
              {formData.content.length}/2000 caratteri
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="expires-at" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Data Scadenza (opzionale)
            </Label>
            <Input
              id="expires-at"
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              className="bg-white max-w-xs"
              data-testid="broadcast-expires-input"
            />
            <p className="text-xs text-slate-400">
              Dopo questa data il messaggio non verrà più mostrato
            </p>
          </div>
          
          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFormData({ title: '', content: '', target_reparto_id: 'all', expires_at: '' })}
              disabled={isSending}
            >
              Reset
            </Button>
            <Button
              type="submit"
              disabled={isSending || !formData.title.trim() || !formData.content.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="broadcast-send-btn"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Invio...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia Messaggio
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Tabella Messaggi Esistenti */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-slate-700">Messaggi Inviati</h4>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchBroadcasts}
              disabled={loadingBroadcasts}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingBroadcasts ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>
          
          {broadcasts.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              Nessun messaggio inviato
            </p>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titolo</TableHead>
                    <TableHead>Destinatari</TableHead>
                    <TableHead>Scadenza</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {broadcasts.map(b => (
                    <TableRow key={b.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{b.title}</p>
                          <p className="text-xs text-slate-400">{formatDate(b.created_at)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {b.target_reparto_name ? (
                          <Badge variant="outline" className="text-xs">
                            {b.target_reparto_name}
                          </Badge>
                        ) : (
                          <Badge className="text-xs bg-blue-100 text-blue-700">Tutti</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {b.expires_at ? (
                          <span className={`text-xs ${b.is_expired ? 'text-red-500' : 'text-slate-600'}`}>
                            {formatDate(b.expires_at)}
                            {b.is_expired && ' (scaduto)'}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {b.is_active !== false ? (
                          <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                        ) : (
                          <Badge variant="secondary">Disattivo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(b)}
                            disabled={actionLoading === b.id}
                            title={b.is_active !== false ? 'Disattiva' : 'Riattiva'}
                          >
                            {b.is_active !== false ? (
                              <EyeOff className="w-4 h-4 text-slate-500" />
                            ) : (
                              <Eye className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(b.id)}
                            disabled={actionLoading === b.id}
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
